import { Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { TreatmentPlanField } from "@components/FieldComponent/TreatmentPlanField";
import {
  useIsClinicalSurface,
  useIsReportCompleted,
  useIsReviewPage,
} from "@components/ReportField/capabilities/flags";
import { StructuralReportFieldProps } from "@components/ReportField/types";
import { FlatTreatmentPlanGoals } from "@pages/ClinicalReportDetails/Components/TreatmentPlan/FlatTreatmentPlanGoals";

// TreatmentPlan is rendered very differently per surface, so this keeps both
// existing implementations as-is rather than unifying their data/layout:
//   - Assessment V2: always shows the domain-grouped goal list.
//   - Clinical (reassessment revamp): goal editing moved off this page, so the
//     flat goal list only appears on review/completed pages. Before that, this
//     used to render nothing, leaving users staring at an empty section
//     (PROA-2105) — show instructional copy instead.
export const ReportFieldTreatmentPlan: React.FC<StructuralReportFieldProps> = ({ field }) => {
  const isClinicalSurface = useIsClinicalSurface();
  const isReviewPage = useIsReviewPage();
  const isCompleted = useIsReportCompleted();

  if (isClinicalSurface) {
    if (!isReviewPage && !isCompleted) {
      return (
        <div
          className="flex w-full flex-col rounded-2xl border border-limestone-100 bg-limestone-50 p-4"
          data-testid="treatment-plan-placeholder"
        >
          <Paragraph displayType="loud" colorType="primary">
            Treatment goals appear here
          </Paragraph>
          <Small colorType="secondary">
            Goals are edited in the reassessment flow and render on the review and signed report.
          </Small>
        </div>
      );
    }
    return <FlatTreatmentPlanGoals />;
  }

  // Medical necessity shows where the clinical surface shows goals: Review and the signed
  // report, not the Report Part 1/2 builder pages (PROA-3018).
  return <TreatmentPlanField fieldTitle={field.title} showMedicalNecessity={isReviewPage || isCompleted} />;
};
